/**
 * New customer — a FULL PAGE at `/countries/:cc/parties/new`, built to
 * `forms.html` screen 3 (WEB TIER A · WT6). A ROUTE, not a nav destination:
 * the `parties` nav row stays `available:false` (phase 3), and this page is
 * reached from the pickers' "create from what you typed" rows — or by URL.
 *
 * ===========================================================================
 * THE RETURN CONTRACT — the other half of the WT5 hand-off
 * ===========================================================================
 * A picker leaves for this page with router state `{ returnTo, name, draft }`
 * (pinned in `inquiry-create.test.tsx`). This page:
 *  · prefills Name from `name`;
 *  · holds `draft` OPAQUE — it never reads inside it, because the quote
 *    builder's draft and the inquiry page's draft are different shapes and
 *    this page serves both;
 *  · on Create, navigates to `returnTo` with `{ party, draft }` — the created
 *    customer chosen and the origin form still filled;
 *  · on Cancel, navigates to `returnTo` with `{ draft }` alone — the origin
 *    survives, nothing is chosen, nothing was written.
 * The mockup's URL spelling `?return=…` is honoured as a FALLBACK (it is what
 * survives a refresh, which loses router state): a `?return=` that starts with
 * `/` is accepted; the draft is gone in that case and the page returns with
 * the party alone. With NO return address at all (a typed URL), success stays
 * here and offers the two write surfaces the new customer is FOR, handing the
 * row over through the same state contract.
 *
 * ===========================================================================
 * SCREEN 3'S ARGUMENT — the form that decides the tax figures, and says so
 * ===========================================================================
 * `state` and `tax_no` are optional and the form SAVES without them — every
 * customer the mockup measured was created with three fields. What it is NOT
 * allowed to do is stay quiet about the cost: for India the absence of both is
 * exactly why quotes price the inter-state IGST line instead of the CGST/SGST
 * pair (the builder's S8 hint is the same fact downstream), so the IN frame
 * carries the warning banner INSIDE the card and repeats the alternative under
 * the State field. Other countries get their own tax word (`taxLabel`) and no
 * borrowed GST story — stating India's split in Dubai would be a lie.
 *
 * FRAME-VS-SCHEMA DIVERGENCES, recorded:
 *  · the mockup's "Commercial link" field is NOT built — `CreatePartyDto`
 *    carries no such column (group linking is `POST /parties/:id/link-tally`
 *    territory, a different act on an existing row);
 *  · the mockup's aside prints its fixture's resolved rate ("IGST 18.000%");
 *    this client does not know the country's tax configuration and INVENTS NO
 *    FIGURE — the aside states the ladder and which rung the typed evidence
 *    reaches, and stops there;
 *  · `customer_country` (XM's required field) is schema-real and not drawn in
 *    the IN-fixture frame — built per the service's own refusal rule.
 *  · "Referred by" (`parties.referral`, migration 49) is schema-real and not in
 *    the frame either — the legacy `RefBy`, restored by owner ruling R4 + the
 *    v11.0 amendment ("belongs on the party"). Optional, omitted when blank,
 *    never required. ⚠ NO LONGER CREATE-ONLY: this line used to record that
 *    E22's `POST /parties/:id/referral` was a 501 blocked on
 *    OQ-PARTY-REFERRAL-WRITE, so an existing customer could not gain one. The
 *    owner ruled 2026-08-18, the route writes, and the PARTY RECORD PAGE
 *    (`PartyRecordPage`, `parties/:partyId`) carries the post-create control —
 *    THIS page remains the create capture only.
 *  · "Address" (`CreatePartyDto.address`) is drawn for a SAF-T (AO) country only (design §3 U3:
 *    the file needs it), and there City / NIF carry the XSD bounds (50 / 30) as `maxLength`.
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import {
  Alert,
  Button,
  KvList,
  KvRow,
  PageHead,
  ReadyBar,
  RecordCard,
  StepCard,
  TextField,
} from '../../components/ui';
import { countryProfile } from '../../domain/countries';
import { SAFT_ADDRESS_MAX, SAFT_CITY_MAX, SAFT_TAX_ID_MAX } from '../../domain/saft-fields';
import { createErrorText } from '../inquiries/errors';
import { createParty } from './api';
import type { CreatePartyBody, PartyDto } from './api';

/** The service's own shape rule (`CreatePartyDto`): two letters, any two. */
const COUNTRY_PATTERN = /^[A-Za-z]{2}$/;

/** What the origin route is CALLED, for the sub-line and the aside. */
function originLabel(returnTo: string): string {
  if (returnTo.endsWith('/quotes/new')) return 'New quote';
  if (returnTo.endsWith('/sales-orders/new')) return 'New sales order';
  if (returnTo.endsWith('/inquiries/new')) return 'New inquiry';
  return 'the page you came from';
}

/** forms.html's `help` line — text/secondary INSIDE a card (the 4.64 pair). */
function Help({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <p className="text-label text-fg-secondary">{children}</p>;
}

function InfoPill({ children }: { children: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center rounded-pill bg-info-bg px-2.5 py-0.5 text-label font-medium text-info-fg">
      {children}
    </span>
  );
}

interface HandOffIn {
  returnTo?: string;
  name?: string;
  /** Opaque — passed back verbatim. See the header. */
  draft?: unknown;
}

export function PartyCreatePage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();

  const profile = countryProfile(countryCode);
  const handOff = (location.state ?? null) as HandOffIn | null;
  const queryReturn = params.get('return');
  /** Router state wins; the `?return=` URL is the refresh-surviving fallback.
   * Only an in-app path (leading `/`) is accepted — anything else is ignored. */
  const returnTo =
    handOff?.returnTo ??
    (queryReturn !== null && queryReturn.startsWith('/') ? queryReturn : null);
  const draft = handOff?.draft;

  /** The service's own list is `INTERNATIONAL_SCOPES = ['XM']`
   * (`parties.service.ts`, §B3) — mirrored here as the one check rather than
   * imported, because the web domain module does not carry the constant and a
   * one-member mirror with its provenance named beats widening that file's
   * surface from this cluster. */
  const isInternational = countryCode === 'XM';
  const isIndia = countryCode === 'IN';
  /** Design §3 U3 — SAF-T (AO) needs an address on every customer, within the XSD bounds. */
  const saftAo = profile.saftAo;

  const [name, setName] = useState(handOff?.name ?? '');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [stateName, setStateName] = useState('');
  const [taxNo, setTaxNo] = useState('');
  const [customerCountry, setCustomerCountry] = useState('');
  /** WHO SENT US THIS CUSTOMER — `parties.referral` (migration 49). Optional; see the
   * field's Help line and `CreatePartyBody.referral`. */
  const [referral, setReferral] = useState('');
  /** The no-return-address success state — see the header. */
  const [created, setCreated] = useState<PartyDto | null>(null);

  const create = useMutation({
    mutationFn: (body: CreatePartyBody) => createParty(countryCode, body),
    onSuccess: (party) => {
      // Both pickers' search caches key on ['parties', cc, …]; the new row must
      // be findable the moment the origin page asks again.
      void queryClient.invalidateQueries({ queryKey: ['parties', countryCode] });
      if (returnTo !== null) {
        navigate(returnTo, {
          state: { party, ...(draft === undefined ? {} : { draft }) },
        });
        return;
      }
      setCreated(party);
    },
  });

  const canCreate = can(countryCode, 'parties.create');
  const cancelHref = returnTo ?? `/countries/${countryCode}/inquiries`;

  if (!canCreate) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Customers cannot be created from this account">
          Creating a customer in {countryCode} needs the Parties create permission. Ask an
          administrator to widen your access.
        </Alert>
        <Link to={cancelHref} className="w-fit text-body13 text-fg underline underline-offset-2">
          Back
        </Link>
      </div>
    );
  }

  /* ── the created-and-nowhere-to-return state ──────────────────────────── */
  if (created !== null) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <PageHead title="New customer" sub={`Created in ${profile.name}.`} />
        <RecordCard title="Created">
          <p className="text-body13 text-fg">
            {created.name} was created. No register lists customers on their own — the row
            exists for the documents it will carry, so the next move is one of these:
          </p>
          <div className="flex flex-wrap gap-3">
            {/* The SAME state contract the return path uses: the write surface
              * opens with this customer already chosen. */}
            <Link
              to={`/countries/${countryCode}/inquiries/new`}
              state={{ party: created }}
              className="text-body13 text-fg underline underline-offset-2 hover:decoration-2"
            >
              Raise an inquiry for them
            </Link>
            <Link
              to={`/countries/${countryCode}/quotes/new`}
              state={{ party: created }}
              className="text-body13 text-fg underline underline-offset-2 hover:decoration-2"
            >
              Start a quote
            </Link>
          </div>
        </RecordCard>
      </div>
    );
  }

  const countryError =
    customerCountry.trim() === '' || COUNTRY_PATTERN.test(customerCountry.trim())
      ? null
      : 'Two letters — an ISO 3166-1 alpha-2 code, e.g. KE for Kenya.';
  const missingCountry = isInternational && !COUNTRY_PATTERN.test(customerCountry.trim());
  const canSubmit =
    name.trim() !== '' && !missingCountry && countryError === null && !create.isPending;

  const submit = (): void => {
    if (!canSubmit) return;
    const body: CreatePartyBody = { name: name.trim() };
    // OMIT-WHEN-BLANK, pinned: '' is a value to the DTO; absence is "not stated".
    if (saftAo && address.trim() !== '') body.address = address.trim();
    if (city.trim() !== '') body.city = city.trim();
    if (phone.trim() !== '') body.phone = phone.trim();
    if (stateName.trim() !== '') body.state = stateName.trim();
    if (taxNo.trim() !== '') body.tax_no = taxNo.trim();
    // The same OMIT-WHEN-BLANK rule: a tabbed-past "Referred by" is "not stated",
    // and the row records NULL. Never required (owner ruling R4).
    if (referral.trim() !== '') body.referral = referral.trim();
    // As typed (owner ruling 3.4, 2026-08-18). This used to `toUpperCase()` "so the
    // request shows what the service will store" — and the service's own fold is now
    // struck, so folding HERE would silently re-case the person's input on the one
    // screen everyone uses and make the server-side strike unreachable from a browser.
    // Readers convert for themselves (`customerOriginLabel` folds its input).
    if (isInternational) body.customer_country = customerCountry.trim();
    create.mutate(body);
  };

  const readyStatement =
    name.trim() === ''
      ? 'A name is required before this can be created.'
      : missingCountry || countryError !== null
        ? 'The customer country is required for GS Multi-trade — the scope cannot say where the customer is.'
        : 'Ready to create. A name is enough; everything else is optional and its cost is stated above.';

  /** Which rung of the ladder the typed evidence reaches (IN aside). No rate
   * and no split figure is claimed — the client does not know the country's
   * tax configuration (header, divergence 2). */
  const evidence =
    taxNo.trim() !== ''
      ? { rung: 'Rung 2 — a tax number is given', branch: 'Its first two digits decide the state.' }
      : stateName.trim() !== ''
        ? { rung: 'Rung 3 — a state is named', branch: 'Compared with the home state to pick the split.' }
        : { rung: 'Rung 5 of 5 — no evidence', branch: 'Falls to the inter-state default.' };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHead
        title="New customer"
        sub={`Nothing is saved yet. Creating in ${profile.name}${
          returnTo === null ? '' : `, returning to ${originLabel(returnTo)}`
        }.`}
        actions={
          <>
            <Link
              to={cancelHref}
              // Cancel carries the draft BACK — the origin form survives the
              // round trip whether or not a customer was created.
              state={draft === undefined ? undefined : { draft }}
              className="inline-flex h-9 items-center rounded-sm border border-rim px-3 text-body font-medium text-fg hover:bg-subtle"
            >
              Cancel
            </Link>
            <Button onClick={submit} disabled={!canSubmit}>
              {create.isPending ? 'Creating…' : 'Create customer'}
            </Button>
          </>
        }
      />

      {create.isError ? <Alert tone="danger">{createErrorText(create.error)}</Alert> : null}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* ═══ the one numbered card ═══════════════════════════════════════ */}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <StepCard n={1} state="now" title="Customer" badge={<InfoPill>Current</InfoPill>}>
            {isIndia ? (
              /* The warning is INSIDE the card, above the fields — it names the
               * consequence in the same pixels as the controls that cause it
               * (screen 3's first note; the same fix the masked register got). */
              <Alert tone="warning">
                State and GSTIN are optional and the form saves without them. The consequence
                is not cosmetic: with neither on record, the place-of-supply ladder falls to
                its last rung and every quote for this customer prices the inter-state IGST
                line instead of the CGST + SGST pair — even when the customer is in your own
                state.
              </Alert>
            ) : null}

            <div className="grid gap-x-4 gap-y-3.5 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <TextField
                  label="Name"
                  value={name}
                  maxLength={200}
                  onChange={(event) => setName(event.target.value)}
                />
                <Help>
                  The name that prints on every document. It is the only field the create
                  call requires.
                </Help>
              </div>

              {saftAo ? (
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <TextField
                    label="Address"
                    value={address}
                    maxLength={SAFT_ADDRESS_MAX}
                    onChange={(event) => setAddress(event.target.value)}
                  />
                  <Help>
                    Angola&rsquo;s SAF-T file needs the customer&rsquo;s address, with a NIF (or
                    999999999) and a city, before a document can be sent, confirmed or printed.
                  </Help>
                </div>
              ) : null}

              <div className="flex flex-col gap-1.5">
                <TextField
                  label="City"
                  value={city}
                  maxLength={saftAo ? SAFT_CITY_MAX : 80}
                  onChange={(event) => setCity(event.target.value)}
                />
                <Help>
                  {isIndia
                    ? 'Recorded, and never read by the tax ladder. A city is not a state.'
                    : 'Recorded on the customer. Shown beside their name in the pickers.'}
                </Help>
              </div>

              <div className="flex flex-col gap-1.5">
                <TextField
                  label="Phone"
                  value={phone}
                  maxLength={40}
                  onChange={(event) => setPhone(event.target.value)}
                />
                <Help>Free text. No format is enforced.</Help>
              </div>

              <div className="flex flex-col gap-1.5">
                <TextField
                  label="State"
                  value={stateName}
                  maxLength={80}
                  placeholder="Leave it blank if unknown"
                  onChange={(event) => setStateName(event.target.value)}
                />
                <Help>
                  {isIndia
                    ? "Blank is legal and saves. Naming the customer's state is what prices " +
                      'the CGST + SGST pair instead of inter-state IGST.'
                    : 'Blank is legal and saves. Free text, recorded on the customer.'}
                </Help>
              </div>

              {/*
               * No tax field for a country without a tax identifier — the DRC, owner ruling
               * N-53, 2026-09-17: "there is no 'NIF' on Congolese documents in this product."
               * The quote PDF prints a customer's number under the COUNTRY's label, and with
               * no label there is no line; a field here would collect what nothing can print.
               */}
              {profile.taxLabel.trim() === '' ? null : (
                <div className="flex flex-col gap-1.5">
                  <TextField
                    label={profile.taxLabel}
                    value={taxNo}
                    maxLength={saftAo ? SAFT_TAX_ID_MAX : 40}
                    placeholder="Leave it blank if unknown"
                    onChange={(event) => setTaxNo(event.target.value)}
                  />
                  <Help>
                    {isIndia
                      ? 'If it is given, its first two digits decide the state and the ladder ' +
                        'stops there.'
                      : `${profile.taxLabel} prints on documents. The server prices tax either way.`}
                  </Help>
                </div>
              )}

              {isInternational ? (
                <div className="flex flex-col gap-1.5">
                  <TextField
                    label="Customer country"
                    value={customerCountry}
                    maxLength={2}
                    placeholder="KE"
                    error={countryError}
                    onChange={(event) => setCustomerCountry(event.target.value)}
                  />
                  {countryError === null ? (
                    <Help>
                      Where the customer is. GS Multi-trade sells where the group has no
                      presence, so the scope cannot infer it — this one is required.
                    </Help>
                  ) : null}
                </div>
              ) : null}

              {/*
                * WHO SENT US THIS CUSTOMER — the legacy party master's `RefBy`, kept
                * because it is the one commercial fact with lasting value, and kept on
                * the PARTY because that is where the owner put it (v11.0: "belongs on
                * the party, not only on the document"). Optional and never required
                * (R4: "capture them, never require them"); the CRF create page prefills
                * from it, so it is typed once here and read on every job for this
                * customer. Full width: it is not one of the tax pair and should not
                * read as one.
                */}
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <TextField
                  label="Referred by"
                  value={referral}
                  maxLength={200}
                  placeholder="A person, a firm, a partner — or Direct"
                  onChange={(event) => setReferral(event.target.value)}
                />
                <Help>
                  Who sent us this customer. Optional and free text — it is recorded on the
                  customer and offered as the starting answer on every CRF raised for them.
                  Blank means nobody said, not that nobody did.
                </Help>
              </div>
            </div>
          </StepCard>

          <ReadyBar statement={readyStatement} />
        </div>

        {/* ═══ the aside ═══════════════════════════════════════════════════ */}
        <aside className="flex w-full shrink-0 flex-col gap-4 lg:w-[360px]">
          {isIndia ? (
            <RecordCard title="What this decides">
              <KvList>
                <KvRow label="Evidence">{evidence.rung}</KvRow>
                <KvRow label="Then">{evidence.branch}</KvRow>
                <KvRow label="Blocking">No — a quote never blocks on a tax question (OD-2)</KvRow>
              </KvList>
              <p className="text-label text-fg-secondary">
                The ladder is: SEZ, then a GSTIN-shaped tax number, then a state code or
                free-text state, then a comparison with the home state, then unresolved. The
                figures themselves are the server&rsquo;s — this page names no rate.
              </p>
            </RecordCard>
          ) : null}

          {returnTo === null ? null : (
            <RecordCard title="Where you came from">
              <p className="text-body13 text-fg-secondary">
                This page was opened from {originLabel(returnTo)} with a return address.
                Creating the customer returns to that page with this customer chosen and the
                form still filled in. It is a route, not a form nested inside a form.
              </p>
            </RecordCard>
          )}

          <RecordCard title="What Create customer does">
            <p className="text-body13 text-fg-secondary">
              One prospect row in parties, in {profile.name}, and a party.created event.
              Nothing else moves — no inquiry, no quote, no number from any document series.
            </p>
          </RecordCard>
        </aside>
      </div>
    </div>
  );
}
